import { Injectable, InjectProperty } from '@jkhy/frontend-utils/di';
import { JhHttpClientCached } from '@jkhy/frontend-utils/http';
import { ObservationSource } from '@jkhy/frontend-utils/observable';
import {
    AccountTransactionsClient,
    CompanyAccountModelDto,
    CompanyAccountsClient,
    DashboardAccountsGroupModelDto,
    DashboardClient,
    DashboardInformationCenterModelDto,
    LookupModelDto,
    NotificationModelDto,
    NotificationsClient,
    ProcessingCutoffDto,
    UserDashboardWidgetModelDto,
} from '@treasury/api/channel';
import { TmHttpClient } from '@treasury/core/http';
import { LoggingService } from '@treasury/core/logging';
import { dateDistance } from '@treasury/utils/functions';
import { ApprovalCounts, ApprovalTypes } from '../approvals/types';
import { Account } from './account.entity';
import { BannerAndModalMessage, isBannerAndModalMessage } from './banner-modal-message.entity';
import { DashboardWidget } from './dashboard-widget.entity';
import { InformationCenterMessage } from './information-center-message.entity';
import { ResourceCenterItem } from './resource-center-item.entity';
import { PaymentType } from './types/payment.types';

// coredata takes account ids as query params, so cap each request to keep a company with many
// accounts under the URL-length limit (the legacy widget batched at the same size).
const COLLECTED_BALANCE_BATCH_SIZE = 75;

@Injectable()
export class DashboardService {
    @InjectProperty()
    declare private readonly dashboardClient: DashboardClient;

    @InjectProperty()
    declare private readonly notificationsClient: NotificationsClient;

    @InjectProperty()
    declare private readonly companyAccountsClient: CompanyAccountsClient;

    @InjectProperty()
    declare private readonly accountTransactionsClient: AccountTransactionsClient;

    @InjectProperty()
    declare private readonly http: TmHttpClient;

    @InjectProperty()
    declare private readonly logger: LoggingService;

    private readonly widgetChangedStream = new ObservationSource<DashboardWidget[]>();

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly widgetChanged$ = this.widgetChangedStream.toObservable();

    public async getConfiguredProducts() {
        const response = await this.dashboardClient.dashboardGetProducts();
        const products = response.data;

        // There's a duplicate ACH product in the list...
        const filteredProducts = products.filter(product => product.id !== 1627726);
        return new Map(filteredProducts.map(p => [p.type, p.status === 'Active']));
    }

    public async clearConfiguredWidgetCache() {
        const http = await TmHttpClient.getInstance();
        if (!(http instanceof JhHttpClientCached)) {
            throw new Error('Cannot reset HTTP cache. The client does not implement caching.');
        }

        http.resetCachedValue(`dashboard/configuredWidgets`, {
            method: 'GET',
        });
        http.resetCachedValue(`dashboard/configuredDashboardWidgets`, {
            method: 'GET',
        });
    }

    public async getConfiguredWidgets(clearCache = true) {
        let response;
        if (clearCache) {
            await this.clearConfiguredWidgetCache();
            response = await this.dashboardClient.dashboardGetConfiguredWidgets();
        } else {
            response = await this.dashboardClient.dashboardGetConfiguredWidgets({
                maxAgeInSeconds: 6000,
            });
        }
        const widgetEntities = response.data.map(widget => new DashboardWidget(widget));
        // sort widgets by left position property, then by top position property
        // left is a column in desktop view, top is a row
        // legacy code states there are 3 total columns available based on the size of the widgets
        return widgetEntities.sort((a, b) => {
            if (a.left === b.left) {
                return a.top - b.top;
            }
            return a.left - b.left;
        });
    }

    public async getConfiguredMobileWidgets(clearCache = true) {
        let data: UserDashboardWidgetModelDto[];
        if (clearCache) {
            await this.clearConfiguredWidgetCache();
            data = (await this.dashboardClient.dashboardGetConfiguredDashboardWidgets()).data;
        } else {
            data = (
                await this.dashboardClient.dashboardGetConfiguredDashboardWidgets({
                    maxAgeInSeconds: 6000,
                })
            ).data;
        }
        return data
            .map(
                widget =>
                    new DashboardWidget({
                        ...widget,
                        updatedDate: new Date().toISOString(),
                    })
            )
            .sort((a, b) => {
                return a.top - b.top;
            });
    }

    public async getAvailableWidgets() {
        const response = await this.dashboardClient.dashboardAvailableWidgets();
        return response.data.map(widget => new DashboardWidget(widget));
    }

    public async getAvailableMobileWidgets() {
        const response = await this.dashboardClient.dashboardAvailableDashboardWidgets();
        return response.data.map(
            widget => new DashboardWidget({ ...widget, updatedDate: new Date().toISOString() })
        );
    }

    public async setConfiguredWidgets(widgets: DashboardWidget[]) {
        const widgetDto = widgets.map(widget => widget.toDto());
        const response = await this.dashboardClient.dashboardSetConfiguredWidgets(widgetDto);
        if (response.ok) {
            await this.clearConfiguredWidgetCache();
            this.widgetChangedStream.emit(widgets);
        }
        return response;
    }

    public async setConfiguredMobileWidgets(widgets: DashboardWidget[]) {
        const widgetDto = widgets.map(widget => ({
            id: widget.id || 0,
            description: widget.description || '',
            hasPermission: widget.hasPermission || false,
            isRequired: widget.isRequired || false,
            permissionMessage: widget.permissionMessage,
            title: widget.title || '',
            top: widget.top || 0,
            type: widget.type || '',
        })) as UserDashboardWidgetModelDto[];

        const response =
            await this.dashboardClient.dashboardSaveConfiguredDashboardWidgets(widgetDto);
        if (response.ok) {
            await this.clearConfiguredWidgetCache();
            this.widgetChangedStream.emit(widgets);
        }
        return response;
    }

    public async clearMyAccountsCache() {
        const http = await TmHttpClient.getInstance();
        if (!(http instanceof JhHttpClientCached)) {
            throw new Error('Cannot reset HTTP cache. The client does not implement caching.');
        }

        http.resetCachedValue(`dashboard/myaccounts`, {
            method: 'GET',
        });
    }

    public async getMyAccounts({ includeCollectedBalance = false } = {}) {
        const response = await this.dashboardClient.dashboardGetAccountGroups({
            maxAgeInSeconds: 60000,
        });
        const groups = response.data;
        // The account-groups endpoint doesn't return collectedBalance; the coredata endpoint does.
        // Only the accounts widget renders that column, so callers opt in to the extra fetch rather
        // than every getMyAccounts consumer paying for it.
        if (includeCollectedBalance) {
            await this.mergeCollectedBalances(groups.flatMap(group => group.accounts ?? []));
        }
        return groups.map((accountGroup: DashboardAccountsGroupModelDto) => {
            const accounts = accountGroup?.accounts?.map(
                (account: CompanyAccountModelDto) => new Account(account)
            );
            return {
                name: accountGroup.name,
                id: accountGroup.id,
                accounts,
            };
        });
    }

    public async createAccountGroup(group: {
        name: string;
        isRemoved: boolean;
        id: number;
        accounts: Account[];
    }) {
        this.clearMyAccountsCache();
        return this.dashboardClient.dashboardCreateAccountGroup({
            ...group,
            updatedDate: new Date().toISOString(),
            accounts: group.accounts.map(account => account.toDto()),
        });
    }

    public async updateAccountGroupName(groupId: number, group: DashboardAccountsGroupModelDto) {
        this.clearMyAccountsCache();
        return this.dashboardClient.dashboardUpdateGroupName(group, { groupId });
    }

    public async deleteAccountGroup(groupId: number) {
        this.clearMyAccountsCache();
        return this.dashboardClient.dashboardDeleteGroup({ groupId });
    }

    public async getAvailableAccounts() {
        const response = await this.dashboardClient.dashboardGetAccounts();
        return response.data.map(account => new Account(account));
    }

    public async addAccountsToGroup(groupId: number, accounts: Account[]) {
        this.clearMyAccountsCache();
        return this.dashboardClient.dashboardAddAccounts(
            accounts.map(account => account.toDto()),
            { groupId }
        );
    }

    public async removeAccountFromGroup(groupId: number, account: Account) {
        this.clearMyAccountsCache();
        return this.dashboardClient.dashboardRemoveAccount({
            groupId,
            accountId: account.toDto().id,
        });
    }

    public async updateAccountGroupOrder(groupId: number, newIndex: number) {
        this.clearMyAccountsCache();
        return this.dashboardClient.dashboardResequenceGroup({ groupId, newIndex });
    }

    public async updateAccountOrder(groupId: number, account: Account, newIndex: number) {
        this.clearMyAccountsCache();
        return this.dashboardClient.dashboardResequenceAccounts({
            groupId,
            accountId: account.toDto().id,
            newIndex,
        });
    }

    public getPendingApprovalPaymentsByType(type: PaymentType) {
        return this.dashboardClient.dashboardGetPaymentPendingApproval({ type });
    }

    public async getPendingApprovalCounts() {
        const { data } = await this.dashboardClient.dashboardPendingApprovalCounts();
        return {
            [ApprovalTypes.ACH]: data.ach,
            [ApprovalTypes.Wire]: data.wires,
            [ApprovalTypes.Loan]: data.loanpayments,
            [ApprovalTypes.Transfer]: data.transfers,
            [ApprovalTypes.StopPayment]: data.stoppayments,
            [ApprovalTypes.User]: data.users,
        } as ApprovalCounts;
    }

    // NOTE: didn't see this one in the API project...
    public logNotificationActivity() {
        return this.http.request(`navigations/auditUserActivity/notifications`, {
            method: 'POST',
        });
    }

    public async markNotificationsAsRead(notificationIds: LookupModelDto[]) {
        return this.notificationsClient.notificationsSetRead(notificationIds, { value: true });
    }

    public async archiveNotifications(notificationIds: LookupModelDto[]) {
        return this.notificationsClient.notificationsArchive(notificationIds);
    }

    public async getRecentNotifications() {
        const response = await this.notificationsClient.notificationsGetRecent();
        if (!response.data.topMessages) {
            return {};
        }
        const { topMessages, totalUnreadCount } = response.data;
        return {
            notifications: topMessages.map((notification: NotificationModelDto) => ({
                ...notification,
                // TODO: move formatting to view model
                createdOn: `${dateDistance(new Date(notification.createdOn), new Date())} ago`,
            })),
            count: totalUnreadCount,
        };
    }

    public async getInformationCenter() {
        const opts = { maxAgeInSeconds: 60 };

        let bannerAndModalPromise = Promise.resolve({
            data: [] as DashboardInformationCenterModelDto[],
        });

        bannerAndModalPromise = this.dashboardClient
            .dashboardGetBannerAndModalMessage(opts)
            // add sequence property so it will be a valid InformationCenterMessage
            // sequence doesn't matter in this context because we are filtering out banners and can only have one modal
            .then(res => ({ data: res.data.map(item => ({ ...item, sequence: 0 })) }));

        const [infoCenter, bannerAndModal] = await Promise.all([
            this.dashboardClient.dashboardGetInformationCenterDetail(opts),
            bannerAndModalPromise,
        ]);

        return [...infoCenter.data, ...bannerAndModal.data]
            .filter(item => item.type !== 'Banner')
            .map(item => new InformationCenterMessage(item));
    }

    public async getInformationCenterBannerModalMessages() {
        const opts = { maxAgeInSeconds: 60 };
        const { data } = await this.dashboardClient.dashboardGetBannerAndModalMessage(opts);

        const modals = data
            .filter(message => message.type === 'Modal')
            .map(modal =>
                isBannerAndModalMessage(modal)
                    ? new BannerAndModalMessage(modal)
                    : new InformationCenterMessage(modal)
            );

        const banners = data
            .filter(message => message.type === 'Banner')
            .map(banner =>
                isBannerAndModalMessage(banner)
                    ? new BannerAndModalMessage(banner)
                    : new InformationCenterMessage(banner)
            );

        return { modal: modals[0], banners };
    }

    public async getResourceCenter() {
        const response = await this.dashboardClient.dashboardGetResourceDetail();
        return response.data.map(item => new ResourceCenterItem(item));
    }

    public async getCutoffTimes() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const timeConversion = (time: any) => {
            let formattedTime = time;
            formattedTime = formattedTime
                .toString()
                .match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [formattedTime];
            if (formattedTime.length > 1) {
                formattedTime = formattedTime.slice(1);
                formattedTime[4] = +formattedTime[0] < 12 ? ' AM' : ' PM';
                formattedTime[0] = +formattedTime[0] % 12 || 12;
            }
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const secondsRemoved = formattedTime.splice(3, 1);
            return formattedTime.join('');
        };
        const response = await this.companyAccountsClient.companyAccountsGetCutoffTimes({
            maxAgeInSeconds: 600,
        });
        if (!response.data) {
            return { cutoffTimes: [], timeZone: '' };
        }
        const cutoffTimes = response.data.processingCutoffs?.map((cutoff: ProcessingCutoffDto) => ({
            cutoffTime: timeConversion(cutoff.cutoffTime) || '',
            label: cutoff.productType || '',
        })) as { cutoffTime: string; label: string }[];

        return {
            cutoffTimes,
            timeZone: response.data.timeZone || '',
        };
    }

    private async mergeCollectedBalances(accounts: CompanyAccountModelDto[]) {
        const ids = accounts
            .map(account => account.id)
            .filter((id): id is number => typeof id === 'number');
        if (!ids.length) {
            return;
        }
        const batches: number[][] = [];
        for (let i = 0; i < ids.length; i += COLLECTED_BALANCE_BATCH_SIZE) {
            batches.push(ids.slice(i, i + COLLECTED_BALANCE_BATCH_SIZE));
        }
        try {
            const responses = await Promise.all(
                batches.map(accountIds =>
                    this.accountTransactionsClient.accountTransactionsGetAccountsCoreData(
                        { accountIds, includeCollectedBalance: true },
                        { maxAgeInSeconds: 60000 }
                    )
                )
            );
            const collectedById = new Map<number, number>();
            responses.forEach(response =>
                response.data.forEach(coreData => {
                    if (coreData.id !== undefined && coreData.collectedBalance !== undefined) {
                        collectedById.set(coreData.id, coreData.collectedBalance);
                    }
                })
            );
            accounts.forEach(account => {
                const collected =
                    account.id !== undefined ? collectedById.get(account.id) : undefined;
                if (collected !== undefined) {
                    account.collectedBalance = collected;
                }
            });
        } catch (e) {
            // Non-fatal: if coredata fails the widget still renders every other column, with
            // collected balance left as the existing $0 rather than failing the whole widget.
            this.logger.logError(
                'Could not fetch collected balances for the accounts widget.',
                e instanceof Error ? e : undefined
            );
        }
    }
}
